import type { CommentBody } from '@liveblocks/client'
import type {
  ComposerEditorComponents,
  ComposerSubmitComment,
} from '@liveblocks/react-ui/primitives'
import { Composer } from '@liveblocks/react-ui/primitives'
import { ArrowUpIcon } from 'lucide-react'
import { type FormEvent, ReactNode, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'

import { COMMENT_COMPOSER_LABELS, overLimitMessage } from './labels'
import { MentionButton } from './mention-button'

type CommentComposerProps = {
  onSubmit: (comment: ComposerSubmitComment) => void | Promise<void>
  placeholder?: string
  notice?: ReactNode
  disabled?: boolean
  editorComponents?: Partial<ComposerEditorComponents>
  defaultValue?: CommentBody
  submitLabel?: string
  onCancel?: () => void
  maxLength?: number
  actionsAppearance?: 'default' | 'compact' | 'icon'
  autoFocus?: boolean
  className?: string
}

export const CommentComposer = ({
  onSubmit,
  placeholder,
  notice,
  disabled,
  editorComponents,
  defaultValue,
  submitLabel = COMMENT_COMPOSER_LABELS.send,
  onCancel,
  maxLength,
  actionsAppearance = 'default',
  autoFocus,
  className,
}: CommentComposerProps) => {
  const [length, setLength] = useState(0)
  const [showLimitError, setShowLimitError] = useState(false)

  const isOverLimit = maxLength !== undefined && length > maxLength
  const buttonSize = actionsAppearance === 'default' ? 'default' : 'sm'

  const handleSubmitAttempt = (event: FormEvent<HTMLFormElement>) => {
    setShowLimitError(isOverLimit)
    if (isOverLimit) event.preventDefault()
  }

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded border border-main-border-input',
        isOverLimit && 'border-main-border-error',
        className,
      )}
    >
      {notice && (
        <div className='flex items-start gap-1 border-b border-main-border-default bg-main-background-muted p-2'>
          {notice}
        </div>
      )}
      <Composer.Form
        disabled={disabled}
        onSubmit={handleSubmitAttempt}
        onComposerSubmit={(comment) => onSubmit(comment)}
        className='flex min-h-24 flex-col gap-1 bg-main-background-card p-2'
      >
        <Composer.Editor
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          components={editorComponents}
          // Liveblocks exposes no text length, so the limit reads the editor's own text.
          onInput={
            maxLength === undefined
              ? undefined
              : (event) => setLength(event.currentTarget.textContent?.length ?? 0)
          }
          className='flex-1 overflow-y-auto break-words text-main-foreground-primary outline-none text-body-default [&_[data-placeholder]]:text-main-foreground-placeholder'
        />
        {showLimitError && maxLength !== undefined && (
          <div role='alert' className='text-main-foreground-danger text-body-small'>
            {overLimitMessage(maxLength)}
          </div>
        )}
        <div className='flex items-center justify-between'>
          <MentionButton />
          <div className='flex items-center gap-2'>
            {onCancel && (
              <Button variant='secondary' size={buttonSize} type='button' onClick={onCancel}>
                {COMMENT_COMPOSER_LABELS.cancel}
              </Button>
            )}
            <Composer.Submit asChild>
              {actionsAppearance === 'icon' ? (
                <Button type='submit' size='icon-sm' aria-label={submitLabel}>
                  <ArrowUpIcon className='size-4' />
                </Button>
              ) : (
                <Button type='submit' size={buttonSize}>
                  {submitLabel}
                </Button>
              )}
            </Composer.Submit>
          </div>
        </div>
      </Composer.Form>
    </div>
  )
}
